"use client";

import * as React from "react";
import { Bot, X, Loader2, RefreshCw, Send } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useLeadForm } from "@/lib/use-lead-form";

/**
 * A lead-capture widget styled as a chat window -- not a live/generative AI
 * conversation. It collects name/email/phone/message through the same
 * validated, captcha-protected pipeline as the signup and demo forms
 * (see useLeadForm), just in a compact single-column layout that fits a
 * narrow floating panel instead of LeadForm's two-column grid.
 */
function ChatWidget() {
  const [open, setOpen] = React.useState(false);
  const { status, feedback, fieldErrors, captcha, captchaAnswer, setCaptchaAnswer, captchaLoading, refreshCaptcha, handleSubmit } = useLeadForm("chatbot");

  return (
    <>
      {open && (
        <div className="fixed bottom-24 right-5 z-40 flex max-h-[calc(100vh-140px)] w-[calc(100vw-40px)] max-w-[340px] flex-col overflow-hidden rounded-xl border border-border bg-card shadow-xl">
          <div className="flex items-center justify-between gap-2 border-b border-border bg-primary px-4 py-3">
            <div className="flex items-center gap-2 text-primary-foreground">
              <span className="flex size-8 items-center justify-center rounded-full bg-white/20">
                <Bot className="size-4" />
              </span>
              <div>
                <p className="text-sm font-semibold">ClinicSuite360</p>
                <p className="text-xs opacity-80">Usually replies within a day</p>
              </div>
            </div>
            <button
              type="button"
              aria-label="Close chat"
              onClick={() => setOpen(false)}
              className="cursor-pointer rounded-md p-1 text-primary-foreground/80 hover:bg-white/10 hover:text-primary-foreground"
            >
              <X className="size-4" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            <div className="mb-4 max-w-[85%] rounded-lg rounded-tl-none bg-muted px-3 py-2 text-sm text-foreground">
              Hi 👋 Have a question about ClinicSuite360? Send us a message below and our team will get back to you by email.
            </div>

            {status === "success" ? (
              <div className="rounded-lg border border-success/30 bg-success-bg px-3 py-4 text-center text-sm text-success">{feedback}</div>
            ) : (
              <form onSubmit={handleSubmit} noValidate className="space-y-3">
                <div className="space-y-1">
                  <Label htmlFor="chat-name" className="text-xs">
                    Name
                  </Label>
                  <Input id="chat-name" name="name" placeholder="Dr. Asha Verma" aria-invalid={!!fieldErrors.name} className="h-9 text-sm" />
                  {fieldErrors.name && <p className="text-xs text-danger">{fieldErrors.name}</p>}
                </div>
                <div className="space-y-1">
                  <Label htmlFor="chat-email" className="text-xs">
                    Email
                  </Label>
                  <Input id="chat-email" name="email" type="email" placeholder="you@yourclinic.com" aria-invalid={!!fieldErrors.email} className="h-9 text-sm" />
                  {fieldErrors.email && <p className="text-xs text-danger">{fieldErrors.email}</p>}
                </div>
                <div className="space-y-1">
                  <Label htmlFor="chat-phone" className="text-xs">
                    Phone
                  </Label>
                  <Input id="chat-phone" name="phone" type="tel" placeholder="+91 98765 43210" aria-invalid={!!fieldErrors.phone} className="h-9 text-sm" />
                  {fieldErrors.phone && <p className="text-xs text-danger">{fieldErrors.phone}</p>}
                </div>
                <div className="space-y-1">
                  <Label htmlFor="chat-message" className="text-xs">
                    Message
                  </Label>
                  <Textarea id="chat-message" name="message" placeholder="Type your question..." rows={2} className="text-sm" />
                </div>

                <div className="space-y-1">
                  <Label htmlFor="chat-captcha" className="text-xs">
                    Verification code
                  </Label>
                  <div className="flex items-center gap-1.5">
                    <div className="flex h-9 w-[110px] shrink-0 items-center justify-center overflow-hidden rounded-md border border-input bg-muted">
                      {captcha ? (
                        // eslint-disable-next-line @next/next/no-img-element -- server-generated data URI, not an optimizable asset.
                        <img src={captcha.image} alt="Verification code" className="h-full w-full object-cover" />
                      ) : (
                        <Loader2 className="size-3.5 animate-spin text-muted-foreground" />
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={refreshCaptcha}
                      disabled={captchaLoading}
                      aria-label="Get a new code"
                      className="cursor-pointer rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-50"
                    >
                      <RefreshCw className={captchaLoading ? "size-3.5 animate-spin" : "size-3.5"} />
                    </button>
                    <Input
                      id="chat-captcha"
                      value={captchaAnswer}
                      onChange={(e) => setCaptchaAnswer(e.target.value)}
                      placeholder="Code"
                      className="h-9 flex-1 text-sm"
                      aria-invalid={!!fieldErrors.captcha_answer}
                    />
                  </div>
                  {fieldErrors.captcha_answer && <p className="text-xs text-danger">{fieldErrors.captcha_answer}</p>}
                </div>

                {status === "error" && feedback && !fieldErrors.captcha_answer && <p className="text-xs text-danger">{feedback}</p>}

                <Button type="submit" size="sm" className="w-full" disabled={status === "submitting"}>
                  {status === "submitting" ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
                  Send message
                </Button>
              </form>
            )}
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? "Close chat" : "Open chat"}
        className="fixed bottom-5 right-24 z-40 flex size-14 cursor-pointer items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-transform hover:scale-105"
      >
        {open ? <X className="size-6" /> : <Bot className="size-7" />}
      </button>
    </>
  );
}

export { ChatWidget };
